import React, { useEffect, useState, useCallback } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { Search, Filter } from 'lucide-react';
import { api } from '../lib/api';
import { formatRelativeTime } from '../lib/utils';
import { useRealtimeRequests } from '../lib/useRealtimeRequests';
import { useNotification, MuteButton } from '../components/NotificationProvider';
import { useBranchStore } from '../store/branchStore';
import { useBranchChangeListener } from '../lib/branchChangeEvent';

interface ServiceRequest {
  id: string;
  ticketNumber: string;
  description: string;
  status: string;
  location: string;
  branchName?: string;
  createdAt: string;
}

export default function Requests() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [requests, setRequests] = useState<ServiceRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState(searchParams.get('filter') || 'open');
  const [searchQuery, setSearchQuery] = useState('');
  const { showNotification } = useNotification();
  const activeBranch = useBranchStore((state) => state.activeBranch);
  const activeBranchId = activeBranch?.id;

  // Update filter when URL changes
  useEffect(() => {
    const urlFilter = searchParams.get('filter');
    if (urlFilter) {
      console.log('[Requests] Filter from URL:', urlFilter);
      setFilter(urlFilter);
    } else {
      // No filter in URL, set to default 'open'
      console.log('[Requests] No filter in URL, defaulting to open');
      setFilter('open');
    }
  }, [searchParams]);

  const fetchRequests = useCallback(async () => {
    if (!activeBranchId) {
      console.log('[Requests] No active branch, skipping fetch');
      return;
    }
    try {
      console.log('[Requests] Fetching requests for branch:', activeBranchId, 'with filter:', filter);
      const params = new URLSearchParams();
      if (filter !== 'all') {
        params.append('status', filter);
      }
      params.append('branchId', activeBranchId);
      const queryString = params.toString() ? `?${params.toString()}` : '';
      const data = await api.get<{ requests: ServiceRequest[] }>(`/requests${queryString}`);
      console.log('[Requests] Received', data.requests.length, 'requests');
      setRequests(data.requests);
    } catch (error) {
      console.error('Failed to fetch requests:', error);
    } finally {
      setLoading(false);
    }
  }, [filter, activeBranchId]);

  useEffect(() => {
    console.log('[Requests] useEffect triggered for branch:', activeBranchId, 'filter:', filter);
    setLoading(true);
    fetchRequests();
  }, [activeBranchId, filter, fetchRequests]);

  // Listen for branch change events from anywhere in the app
  // The event provides the branch, so we don't need to wait for state update
  useBranchChangeListener(useCallback((branch) => {
    console.log('[Requests] Branch change event received:', branch.id, branch.name);
    setLoading(true);
    // Fetch data directly for the branch in the event (not relying on activeBranchId state)
    (async () => {
      try {
        const params = new URLSearchParams();
        if (filter !== 'all') {
          params.append('status', filter);
        }
        params.append('branchId', branch.id);
        const queryString = params.toString() ? `?${params.toString()}` : '';
        console.log('[Requests] Event: Fetching requests for branch:', branch.id, 'with filter:', filter);
        const data = await api.get<{ requests: ServiceRequest[] }>(`/requests${queryString}`);
        console.log('[Requests] Event: Received', data.requests.length, 'requests');
        setRequests(data.requests);
        setLoading(false);
      } catch (error) {
        console.error('[Requests] Event fetch failed:', error);
        setLoading(false);
      }
    })();
  }, [filter]));

  // Subscribe to realtime request updates
  useRealtimeRequests({
    branchId: activeBranchId,
    onInitialLoad: (initialRequests) => {
      console.log('[Requests] Realtime: Initial load of', initialRequests.length, 'requests');
      setRequests(initialRequests);
      setLoading(false);
    },
    onNewRequest: (request) => {
      console.log('[Requests] Realtime: New request detected:', request.ticketNumber);
      setRequests((prev) => [request, ...prev]);
      showNotification('info', `New request: ${request.ticketNumber} (#${request.description})`);
    },
    onStatusChange: (request, oldStatus, newStatus) => {
      console.log('[Requests] Realtime: Status changed:', request.ticketNumber, `${oldStatus} → ${newStatus}`);
      setRequests((prev) =>
        prev.map((req) =>
          req.id === request.id ? { ...req, status: newStatus } : req
        )
      );
      const statusLabels: Record<string, string> = {
        open: 'Open',
        in_progress: 'In Progress',
        pending: 'Pending',
        completed: 'Completed',
      };
      showNotification(
        'success',
        `Status updated: ${request.ticketNumber} - ${statusLabels[oldStatus] || oldStatus} → ${statusLabels[newStatus] || newStatus}`
      );
    },
  });

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'open': return 'bg-blue-100 text-blue-800';
      case 'in_progress': return 'bg-amber-100 text-amber-800';
      case 'pending': return 'bg-orange-100 text-orange-800';
      case 'completed': return 'bg-emerald-100 text-emerald-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };


  const formatStatus = (status: string) => {
    return status.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
  };

  const filteredRequests = requests.filter(req =>
    req.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
    req.ticketNumber.toLowerCase().includes(searchQuery.toLowerCase()) ||
    req.location.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="p-4 md:p-8">
      <div className="mb-8 flex flex-col md:flex-row md:items-start md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Service Requests</h1>
          <p className="text-gray-500 mt-1">{requests.length} total requests</p>
        </div>
        <MuteButton />
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm p-4 md:p-6 mb-6 border border-gray-200">
        <div className="flex flex-col gap-4 mb-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search requests..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
            />
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          {['all', 'open', 'in_progress', 'pending', 'completed'].map((status) => (
            <button
              key={status}
              onClick={() => {
                setFilter(status);
                setSearchParams(status !== 'all' ? { filter: status } : {});
              }}
              className={`px-3 md:px-4 py-2 rounded-lg text-xs md:text-sm font-medium transition-colors ${
                filter === status
                  ? 'bg-cyan-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {formatStatus(status)}
            </button>
          ))}
        </div>
      </div>

      {/* Requests - Responsive Layout */}
      <div className="space-y-4">
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-600"></div>
          </div>
        ) : filteredRequests.length === 0 ? (
          <div className="bg-white rounded-xl shadow-sm p-8 md:p-12 border border-gray-200 text-center text-gray-500">
            No requests found
          </div>
        ) : (
          <>
            {/* Desktop Table View */}
            <div className="hidden md:block bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Ticket
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Title
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Location
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Created
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredRequests.map((request) => (
                    <tr
                      key={request.id}
                      className="hover:bg-gray-50 cursor-pointer transition-colors"
                      onClick={() => navigate(`/requests/${request.id}`)}
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="text-sm font-medium text-cyan-600">
                          {request.ticketNumber}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm font-medium text-gray-900">{request.ticketNumber}</div>
                        <div className="text-sm text-gray-500 truncate max-w-md">{request.description}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(request.status)}`}>
                          {formatStatus(request.status)}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-700 font-medium">{request.location}</div>
                        {request.branchName && (
                          <div className="text-xs text-gray-500 mt-1">{request.branchName}</div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatRelativeTime(request.createdAt)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile Card View */}
            <div className="md:hidden space-y-4">
              {filteredRequests.map((request) => (
                <div
                  key={request.id}
                  onClick={() => navigate(`/requests/${request.id}`)}
                  className="bg-white rounded-xl shadow-sm p-4 border border-gray-200 hover:shadow-md transition-all cursor-pointer"
                >
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h3 className="text-sm font-semibold text-gray-900">#{request.ticketNumber}</h3>
                      <p className="text-xs text-gray-500 mt-1">{request.status}</p>
                    </div>
                  </div>
                  <p className="text-sm text-gray-600 mb-3 line-clamp-2">{request.description}</p>
                  <div className="flex items-center justify-between text-xs text-gray-500">
                    <span className={`px-2 py-1 rounded-full ${getStatusColor(request.status)} text-white text-xs font-semibold`}>
                      {formatStatus(request.status)}
                    </span>
                    <span>{request.location}</span>
                    <span>{formatRelativeTime(request.createdAt)}</span>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
